// update-phone.component.ts
import { HttpClient } from "@angular/common/http";
import { Component, HostListener, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { ConfigService } from "src/app/services/config.service";

@Component({
  selector: "vex-update-phone",
  templateUrl: "./update-phone.component.html",
  styleUrls: ["./update-phone.component.scss"],
})
export class UpdatePhoneComponent implements OnInit {
  readonly noPhoneValue = "None Provided";
  readonly noPhoneLabel = "No phone number is currently on file.";

  myForm!: FormGroup;

  isPhoneInput = false;
  isCellPhoneInput = false;
  isWorkPhoneInput = false;
  isLoading: boolean;
  applicantID: string = "";
  applicantName: string = "";
  gender: string = "";
  applicantAddress: string = "";
  dob: string = "";
  apiUrl: any;
  phone: string = "";
  altPhone: string = "";
  workPhone: string = "";
  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private toastr: ToastrService,
        private router: Router,
    private apiConfig: ConfigService
  ) {}

  isMobile = window.innerWidth < 768;

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  ngOnInit(): void {
    this.apiUrl = this.apiConfig.get('apiUrl');
    this.route.queryParams.subscribe((params) => {
      this.applicantID = params["applicantID"] || "";
      this.applicantName = params["applicantName"] || "";
      this.gender = params["gender"] || "";
      this.applicantAddress = params["address"] || "";
      this.dob = params["dob"] || "";
      this.phone = params["phone"] || "";
      this.altPhone = params["altPhone"] || "";
      this.workPhone = params["workPhone"] || "";
      this.initForm();
    });
  }

  private initForm(): void {
    const home = this.resolvePhoneValue(this.phone);
    const cell = this.resolvePhoneValue(this.altPhone);
    const work = this.resolvePhoneValue(this.workPhone);

    this.isPhoneInput = home.useInput;
    this.isCellPhoneInput = cell.useInput;
    this.isWorkPhoneInput = work.useInput;

    this.myForm = new FormGroup({
      phone: new FormControl(home.display, home.useInput
        ? [Validators.required, this.phoneValidator]
        : [Validators.required]),
      altPhone: new FormControl(cell.display, [this.phoneValidator]),
      workPhone: new FormControl(work.display, [this.phoneValidator]),
    });
  }

  private isRealPhone(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return (
      trimmed !== "" &&
      trimmed !== this.noPhoneValue &&
      trimmed.toLowerCase() !== "null"
    );
  }

  private formatPhoneNumber(value: string): string {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 10) return value;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private resolvePhoneValue(raw: string): { display: string; useInput: boolean } {
    if (!this.isRealPhone(raw)) {
      return { display: this.noPhoneValue, useInput: false };
    }
    return { display: this.formatPhoneNumber(raw), useInput: true };
  }

  phoneValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || value === this.noPhoneValue) return null;
    const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phonePattern.test(value) ? null : { invalidPhone: true };
  };

  onPhoneSelectionChange(value: string) {
    this.isPhoneInput = value === "Enter Phone";
    const control = this.myForm.get("phone");
    control?.setValue(this.isPhoneInput ? "" : this.noPhoneValue);
    control?.setValidators(
      this.isPhoneInput
        ? [Validators.required, this.phoneValidator]
        : [Validators.required]
    );
    control?.updateValueAndValidity();
  }

  onAltPhoneSelectionChange(value: string) {
    this.isCellPhoneInput = value === "Enter Phone";
    const control = this.myForm.get("altPhone");
    control?.setValue(this.isCellPhoneInput ? "" : this.noPhoneValue);
    control?.updateValueAndValidity();
  }

  onWorkPhoneSelectionChange(value: string) {
    this.isWorkPhoneInput = value === "Enter Phone";
    const control = this.myForm.get("workPhone");
    control?.setValue(this.isWorkPhoneInput ? "" : this.noPhoneValue);
    control?.updateValueAndValidity();
  }

  onSubmit() {
    this.myForm.markAllAsTouched();

    if (this.myForm.invalid) {
      console.warn("Form is invalid:", this.myForm.errors);
      return;
    }

    this.isLoading = true;

    const url = `${this.apiUrl}/admin/applicant/update-phone/${this.applicantID}`;
    const payload = this.myForm.value;

    this.http.post(url, payload).subscribe({
      next: (res) => {
        this.toastr.success("Contact details updated successfully", "Success");

        this.myForm.reset({
          phone: this.noPhoneValue,
          altPhone: this.noPhoneValue,
          workPhone: this.noPhoneValue,
        });

        this.isPhoneInput = false;
        this.isCellPhoneInput = false;
        this.isWorkPhoneInput = false;
        this.isLoading = false;
          this.router.navigate(['/applicant/thank-you']);
      },
      error: (err) => {
        console.error("Update failed:", err);
        this.toastr.error("Failed to update contact details", "Error");
        this.isLoading = false;
      },
    });
  }
}
